import { Link } from "react-router-dom";

export default function FacilityManagement() {
  return (
    <div className="min-h-screen bg-[#080808] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#080808]/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 bg-gradient-to-br from-[#00E5FF] to-[#00B8D4] rounded flex items-center justify-center">
              <i className="ri-building-4-line text-xl text-[#080808]"></i>
            </div>
            <span className="font-['Barlow_Condensed'] text-2xl font-bold tracking-wide">SURE KLEEN</span>
          </Link>
          <Link to="/" className="text-sm font-['Space_Grotesk'] text-white/70 hover:text-[#00E5FF] transition-colors whitespace-nowrap">
            <i className="ri-arrow-left-line mr-2"></i>Back to Home
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden mt-16">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/6121a4711595e85b35b7e24b7293644a.jpg"
            alt="Facility Management"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#080808]"></div>
        </div>
        <div className="relative z-10 text-center max-w-4xl px-6">
          <div className="inline-block px-4 py-2 bg-[#00E5FF]/10 border border-[#00E5FF]/30 rounded-full mb-6">
            <span className="font-['Space_Mono'] text-sm text-[#00E5FF] tracking-wider">NAICS 561210</span>
          </div>
          <h1 className="font-['Barlow_Condensed'] text-6xl md:text-7xl font-bold mb-6 tracking-tight">
            Integrated Facility Management
          </h1>
          <p className="font-['Space_Grotesk'] text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
            Comprehensive facility operations management for multi-site portfolios and complex properties
          </p>
        </div>
      </section>

      {/* What We Deliver */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-['Barlow_Condensed'] text-5xl font-bold mb-16 text-center">
            What We <span className="text-[#00E5FF]">Deliver</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: "ri-building-line",
                title: "Multi-Site Coordination",
                desc: "Centralized management of cleaning, maintenance, and vendor services across property portfolios"
              },
              {
                icon: "ri-calendar-check-line",
                title: "Preventive Maintenance",
                desc: "Scheduled inspections and proactive repairs to minimize downtime and extend asset life"
              },
              {
                icon: "ri-team-line",
                title: "Vendor Management",
                desc: "Coordination of specialized contractors, quality control, and invoice reconciliation"
              },
              {
                icon: "ri-file-chart-line",
                title: "Performance Reporting",
                desc: "Real-time dashboards, KPI tracking, and executive summaries for portfolio oversight"
              },
              {
                icon: "ri-customer-service-2-line",
                title: "24/7 Emergency Response",
                desc: "On-call support for urgent issues, natural disasters, and facility emergencies"
              },
              {
                icon: "ri-funds-line",
                title: "Budget Optimization",
                desc: "Cost analysis, procurement strategies, and operational efficiency improvements"
              }
            ].map((item, idx) => (
              <div key={idx} className="bg-white/5 border border-white/10 rounded-lg p-8 hover:border-[#00E5FF]/50 transition-all group">
                <div className="w-14 h-14 bg-[#00E5FF]/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#00E5FF]/20 transition-colors">
                  <i className={`${item.icon} text-3xl text-[#00E5FF]`}></i>
                </div>
                <h3 className="font-['Space_Grotesk'] text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-white/70 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-24 px-6 bg-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-['Barlow_Condensed'] text-5xl font-bold mb-8">
            Why It <span className="text-[#00E5FF]">Matters</span>
          </h2>
          <p className="font-['Space_Grotesk'] text-lg text-white/80 leading-relaxed mb-6">
            Managing multiple facilities or complex properties requires coordination across dozens of vendors, compliance requirements, and operational systems. Fragmented management leads to inefficiency, cost overruns, and service gaps.
          </p>
          <p className="font-['Space_Grotesk'] text-lg text-white/80 leading-relaxed">
            Sure Kleen's integrated facility management provides a single point of accountability—streamlining operations, reducing costs, and ensuring consistent service delivery across your entire portfolio.
          </p>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-['Barlow_Condensed'] text-5xl font-bold mb-16 text-center">
            Our <span className="text-[#00E5FF]">Process</span>
          </h2>
          <div className="space-y-8">
            {[
              {
                step: "01",
                title: "Portfolio Assessment",
                desc: "Comprehensive audit of current operations, vendor contracts, and service gaps across all sites"
              },
              {
                step: "02",
                title: "Strategic Planning",
                desc: "Development of standardized procedures, KPIs, and service level agreements"
              },
              {
                step: "03",
                title: "Transition Management",
                desc: "Phased implementation with minimal disruption to ongoing operations"
              },
              {
                step: "04",
                title: "Ongoing Operations",
                desc: "Daily coordination, quality inspections, and vendor oversight across all locations"
              },
              {
                step: "05",
                title: "Continuous Improvement",
                desc: "Quarterly business reviews, cost optimization analysis, and service enhancement initiatives"
              }
            ].map((item, idx) => (
              <div key={idx} className="flex gap-8 items-start group">
                <div className="flex-shrink-0 w-20 h-20 bg-gradient-to-br from-[#00E5FF] to-[#00B8D4] rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <span className="font-['Barlow_Condensed'] text-2xl font-bold text-[#080808]">{item.step}</span>
                </div>
                <div className="flex-1 pt-2">
                  <h3 className="font-['Space_Grotesk'] text-2xl font-bold mb-3">{item.title}</h3>
                  <p className="text-white/70 leading-relaxed text-lg">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Standards & Compliance */}
      <section className="py-24 px-6 bg-white/5">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-['Barlow_Condensed'] text-5xl font-bold mb-16 text-center">
            Standards & <span className="text-[#00E5FF]">Compliance</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: "ri-government-line",
                title: "SBA 8(a) Certified",
                desc: "Qualified for federal facility management contracts and GSA schedules"
              },
              {
                icon: "ri-shield-check-line",
                title: "Bonded & Insured",
                desc: "Comprehensive coverage for multi-site operations and high-value properties"
              },
              {
                icon: "ri-file-shield-2-line",
                title: "Compliance Management",
                desc: "OSHA, EPA, and industry-specific regulatory adherence across all sites"
              }
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="w-20 h-20 bg-[#00E5FF]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${item.icon} text-4xl text-[#00E5FF]`}></i>
                </div>
                <h3 className="font-['Space_Grotesk'] text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-white/70 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-['Barlow_Condensed'] text-5xl font-bold mb-6">
            Simplify Your Facility Operations
          </h2>
          <p className="font-['Space_Grotesk'] text-xl text-white/80 mb-10">
            Let Sure Kleen provide integrated management for your entire property portfolio.
          </p>
          <a
            href="/#contact"
            className="inline-block px-10 py-4 bg-gradient-to-r from-[#00E5FF] to-[#00B8D4] text-[#080808] font-['Space_Grotesk'] font-bold text-lg rounded-lg hover:shadow-[0_0_30px_rgba(0,229,255,0.5)] transition-all whitespace-nowrap"
          >
            Request Consultation
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="font-['Space_Mono'] text-sm text-white/50">
            © 2026 Sure Kleen Services. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}